/**
 * The HDR output chain: exposure, anti-aliasing, bloom, ACES.
 *
 * ## The order, and why each step is where it is
 *
 *   scene → **exposure** → **TRAA** → **bloom** → **ACES + sRGB**
 *
 * **Exposure first.** It has to be applied before bloom, because bloom is a *threshold* effect:
 * it takes whatever is brighter than some value and blooms it. If exposure came after, the
 * threshold would be measured against un-exposed radiance, and since this project's radiance
 * values are physical (a sunlit surface sits around 0.1, the sun's disc around 25 — see
 * `atmosphere/model.ts`), the same threshold would bloom nothing at dusk and bloom the entire
 * sky at noon. Applying exposure first means the threshold is in *display* units and means the
 * same thing at every time of day.
 *
 * **TRAA before bloom.** Temporal anti-aliasing works by jittering the camera sub-pixel and
 * accumulating frames. Bloom applied before that gets jittered along with everything else and
 * the halo around the sun crawls. Resolve first, then bloom the stable image.
 *
 * **Tone mapping last**, which is three's default for `RenderPipeline.outputNode`: ACES maps the
 * unbounded HDR range onto the display and does the sRGB transfer. Everything upstream is
 * linear and unbounded, which is the only way the sun can legitimately be two hundred times
 * brighter than the terrain and still both be on screen.
 *
 * ## Bloom, kept on a leash
 *
 * CLAUDE.md is specific: "threshold high — sun glints and afterburner only". Bloom is the single
 * easiest way to make a render look cheap; the failure mode is a soft glow over the whole image
 * that reads as a smeared lens rather than as bright light. The threshold here is set above
 * anything a *surface* can reach after exposure, so the only things that pass it are actual
 * light sources: the solar disc, the glitter path off the water, runway lights at night, and —
 * when M5 lands them — afterburners and tracers.
 *
 * ## Anti-aliasing per tier
 *
 * TRAA at Medium and above. At Low it is replaced by FXAA, which is a fraction of the cost and
 * needs no velocity buffer or history — and note it runs *after* the output transform, because
 * FXAA's edge detection is calibrated on perceptual (sRGB) values and applying it to linear HDR
 * finds edges in the wrong places. That is why `outputColorTransform` is switched off on that
 * path and the transform is done explicitly.
 *
 * ## The frame, drawn explicitly
 *
 * The scene is rendered into a target this class owns rather than by a `pass(scene, camera)` node,
 * and that is a deliberate reversal of what three's post-processing wants. The volumetric cloud
 * layer needs **this frame's** scene depth — a cloud in front of a mountain has to be occluded by
 * it — and it renders at half resolution into its own buffer, so it cannot be an object in the
 * scene. With `pass()` owning the scene render, the only place to put the cloud pass is inside the
 * node graph, where three silently dropped it (see the note at the top of
 * `clouds/volumetricClouds.ts` — `setup()` was never called and nothing said so).
 *
 * Owning the scene target instead makes the whole frame one readable sequence:
 *
 *   scene → clouds (reads scene depth) → composite → exposure → TRAA → bloom → ACES
 *
 * with the first two steps being ordinary imperative calls in {@link PostChain.render} and only the
 * image-processing tail left as nodes. Nothing about the ordering is implicit, and nothing in it
 * can fail without failing loudly.
 *
 * `updateFrame(alpha)`: yes — {@link PostChain.setExposure} is pushed each frame from the
 * environment. The chain itself is rebuilt only on a tier change.
 */

import {
  ColorManagement,
  DepthTexture,
  HalfFloatType,
  LinearFilter,
  NoToneMapping,
  RenderTarget,
  Vector2,
  type PerspectiveCamera,
  type Scene,
} from 'three';
import { RenderPipeline, WebGPURenderer } from 'three/webgpu';
import { mrt, output, renderOutput, texture, uniform, vec4, velocity } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { fxaa } from 'three/addons/tsl/display/FXAANode.js';
import { traa } from 'three/addons/tsl/display/TRAANode.js';
import type { Node } from 'three/webgpu';
import type { QualitySettings } from '../core/quality.js';
import type { VolumetricClouds } from './clouds/volumetricClouds.js';

/**
 * Bloom threshold, in display units after exposure.
 *
 * Middle grey lands at 0.2 and a white surface in direct sun at roughly 0.8, so a threshold of
 * 1.15 is comfortably above anything that is merely *lit* and below anything that is *emitting*.
 */
const BLOOM_THRESHOLD = 1.15;
const BLOOM_STRENGTH = 0.42;
const BLOOM_RADIUS = 0.55;

/**
 * The scene render's output layout when TRAA is on: colour to attachment 0, motion vectors to 1.
 *
 * Built once at module scope. `mrt()` produces a node, and handing the renderer a freshly built one
 * every frame would put a node construction in the hot path for a value that never changes.
 */
const SCENE_MRT = /* #__PURE__ */ mrt({ output, velocity });

export interface PostChainOptions {
  renderer: WebGPURenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  settings: QualitySettings;
  /** The volumetric cloud layer, composited inside the chain. Null disables it entirely. */
  clouds?: VolumetricClouds | null;
  /**
   * Show an intermediate buffer instead of the composited frame.
   *
   * `clouds` shows the raw half-resolution cloud target: in-scattering in RGB, and the layer's
   * transmittance in alpha shown as brightness. Worth keeping in the shipped build for the same
   * reason the `__SKYSIM__` handle is — when the cloud layer looks wrong, the only two questions
   * are "did the pass run" and "what did it produce", and nothing else in the frame can answer
   * either. Reached with `?debug=clouds`.
   */
  debug?: 'none' | 'clouds' | 'cloudAlpha';
}

export class PostChain {
  private readonly renderer: WebGPURenderer;
  private readonly scene: Scene;
  private readonly camera: PerspectiveCamera;
  private readonly clouds: VolumetricClouds | null;
  private readonly debug: 'none' | 'clouds' | 'cloudAlpha';

  private pipeline: RenderPipeline | null = null;
  private readonly exposure = uniform(1);
  private settings: QualitySettings;

  /**
   * The scene buffer, owned here.
   *
   * Colour is half-float because the whole chain upstream of ACES is unbounded physical radiance
   * (a sunlit surface ≈ 0.1, the solar disc ≈ 25 — see `atmosphere/model.ts`), and an 8-bit target
   * would clip the sun to flat white before tone mapping ever saw it. The depth texture is what
   * the cloud pass occludes against; the second colour attachment carries motion vectors when TRAA
   * is on.
   */
  private sceneTarget: RenderTarget | null = null;
  private readonly viewportSize = new Vector2();

  /** False when the chain could not be built and the caller must render directly. */
  private enabledValue = true;

  /**
   * Split the last frame's render step into its three passes [ms].
   *
   * The loop charges everything between `frameUpdate` and the audio step to `renderCPU`, which is
   * the right granularity for a budget and the wrong one for a stutter: a long frame attributed to
   * `render()` names a function that contains the whole scene, a raymarch and a post chain, and
   * "somewhere in there" is not a location. These three numbers are the location.
   *
   * Filled only while {@link measurePhases} is on — three `performance.now()` calls a frame is
   * nothing next to what they explain, but a normal session has nobody to read them, and the
   * project's rule is that instruments which cost anything are opt-in. Same arrangement as the
   * allocation probe.
   */
  readonly phases = { sceneMs: 0, cloudsMs: 0, postMs: 0 };

  /** Turn {@link phases} on. Set by the perf harness's owner; off in a normal session. */
  measurePhases = false;

  constructor(options: PostChainOptions) {
    this.renderer = options.renderer;
    this.scene = options.scene;
    this.camera = options.camera;
    this.clouds = options.clouds ?? null;
    this.debug = options.debug ?? 'none';
    this.settings = options.settings;
    this.build();
  }

  /** True when {@link render} will do the work; false means fall back to `renderer.render`. */
  get enabled(): boolean {
    return this.enabledValue && this.pipeline !== null;
  }

  private build(): void {
    this.pipeline?.dispose();
    this.pipeline = null;

    const post = this.settings.post;

    try {
      /*
       * Motion vectors, but only when something is going to read them.
       *
       * An extra render target the width of the screen is not free, and at Low — where TRAA is
       * off — nothing would ever sample it. Allocating the second attachment unconditionally is a
       * quiet per-frame cost on exactly the tier that can least afford one.
       */
      const depthTexture = new DepthTexture(1, 1);
      depthTexture.name = 'SkySim.scene.depth';
      const sceneTarget = new RenderTarget(1, 1, {
        type: HalfFloatType,
        count: post.taa ? 2 : 1,
        depthTexture,
      });
      /*
       * The attachment names are `output` and `velocity`, and they are not decoration.
       *
       * `MRTNode.setup()` resolves each key of the MRT it was given against the bound render
       * target's attachments **by `texture.name`**, and skips — silently, with a comment saying so
       * — any key it cannot find. Name these anything else and every member is skipped, the
       * generated fragment struct comes out empty, and WGSL rejects it with "structures must have
       * at least one member" for every material in the scene. Which is to say: the whole world
       * stops drawing because a debug label did not match a magic string.
       */
      sceneTarget.texture.name = 'output';
      for (const attachment of sceneTarget.textures) {
        attachment.minFilter = LinearFilter;
        attachment.magFilter = LinearFilter;
      }
      if (post.taa) sceneTarget.textures[1].name = 'velocity';
      this.sceneTarget = sceneTarget;
      this.resizeSceneTarget();

      const sceneColor = texture(sceneTarget.texture);
      const sceneDepth = texture(depthTexture);
      // `let` on a vec4 node rather than reassigning the texture node: the composite below
      // produces a plain vec4, which is not a TextureNode and must not be typed as one.
      let color: Node<'vec4'> = vec4(sceneColor);

      /*
       * Clouds, composited before exposure.
       *
       * The pass itself has already run by the time this shader executes — {@link render} draws it
       * between the scene and the composite — so all this does is read the buffer it produced.
       *
       * The composite is `scene · transmittance + scattering` — attenuate what is behind the
       * cloud, then add what the cloud itself is sending toward the eye. Identical in form to
       * the aerial perspective's, because it is the same physics with a much denser medium.
       */
      if (this.clouds !== null && this.settings.clouds.mode === 'volumetric') {
        this.clouds.setSceneDepth(depthTexture);
        const cloud = this.clouds.compositeNode;
        if (this.debug === 'clouds') {
          color = vec4(cloud.rgb, 1);
        } else if (this.debug === 'cloudAlpha') {
          // Transmittance as brightness: white is clear air, black is opaque cloud.
          color = vec4(cloud.a, cloud.a, cloud.a, 1);
        } else {
          color = vec4(color.rgb.mul(cloud.a).add(cloud.rgb), color.a);
        }
      }

      // Exposure, before anything looks at magnitudes. See the note at the top of this file.
      let node: Node<'vec4'> = color.mul(this.exposure);

      if (post.taa) {
        node = traa(node, sceneDepth, texture(sceneTarget.textures[1]), this.camera);
      }

      if (post.bloom) {
        node = node.add(bloom(node, BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD));
      }

      const pipeline = new RenderPipeline(this.renderer);

      if (!post.taa) {
        // FXAA wants perceptual values, so the output transform happens first and explicitly.
        pipeline.outputColorTransform = false;
        pipeline.outputNode = fxaa(renderOutput(node));
      } else {
        pipeline.outputColorTransform = true;
        pipeline.outputNode = node;
      }

      pipeline.needsUpdate = true;
      this.pipeline = pipeline;
      this.enabledValue = true;
    } catch (error) {
      /*
       * A post chain that fails to build must not take the whole simulator with it.
       *
       * The chain depends on optional backend features — multiple render targets, float render
       * targets, a depth texture that can be sampled — and the WebGL2 fallback path does not
       * support the same set as WebGPU. Non-negotiable #5 says never ship a feature that breaks
       * the WebGL2 path, so if the chain cannot be built the renderer draws straight to the
       * canvas and the caller applies exposure through `toneMappingExposure` instead. Uglier;
       * still flying.
       */
      console.warn('OpenSkies: the post-processing chain could not be built —', error);
      this.pipeline = null;
      this.enabledValue = false;
    }
  }

  /**
   * The multiplier applied to the whole HDR image.
   *
   * When the chain is running this is a uniform inside it; when it is not, it falls back to the
   * renderer's own tone-mapping exposure, which is applied at the same point in the pipeline and
   * so produces the same image, just without anything downstream of it.
   */
  setExposure(exposure: number): void {
    this.exposure.value = exposure;
    if (!this.enabled) this.renderer.toneMappingExposure = exposure;
  }

  applyQuality(settings: QualitySettings): void {
    const previous = this.settings;
    this.settings = settings;
    // Only the structural switches need a rebuild; a tier step that changes none of them costs
    // nothing, and rebuilding compiles fullscreen pipelines, which is the sort of hitch the
    // governor changed tier to avoid in the first place. The cloud *mode* is structural because
    // it decides whether the composite samples the cloud buffer at all — a tier that has no
    // volumetric layer must not multiply the scene by a buffer nobody is writing.
    this.clouds?.applyQuality(settings);
    if (
      previous.post.taa !== settings.post.taa ||
      previous.post.bloom !== settings.post.bloom ||
      previous.clouds.mode !== settings.clouds.mode
    ) {
      this.build();
      // The exposure uniform is recreated with the chain; re-publish it or the first frame
      // after a tier step is rendered at whatever it defaulted to.
      this.setExposure(this.exposure.value as number);
    }
  }

  /**
   * Keep the scene buffer the size of the drawing buffer.
   *
   * Checked every frame rather than driven off the resize observer, because the renderer's drawing
   * buffer also moves when the quality tier changes the render scale or the pixel-ratio cap, and
   * those do not go through a DOM resize.
   */
  private resizeSceneTarget(): void {
    const target = this.sceneTarget;
    if (target === null) return;
    this.renderer.getDrawingBufferSize(this.viewportSize);
    const width = Math.max(1, Math.floor(this.viewportSize.x));
    const height = Math.max(1, Math.floor(this.viewportSize.y));
    if (target.width !== width || target.height !== height) {
      target.setSize(width, height);
    }
  }

  /**
   * Draw the frame. Returns false when the caller must render directly instead.
   *
   * The three steps are in this order for a reason and the order is the whole point of the class:
   * the clouds read the depth the scene just wrote, and the composite reads the buffer the clouds
   * just wrote.
   */
  render(): boolean {
    const pipeline = this.pipeline;
    const sceneTarget = this.sceneTarget;
    if (pipeline === null || sceneTarget === null) return false;

    this.resizeSceneTarget();

    /*
     * Draw the scene and the clouds linearly, with the output transform switched off.
     *
     * This is not optional and it is not obvious. Everything upstream of the chain's last step is
     * unbounded physical radiance, and the tone map + sRGB transfer belong at the very end — which
     * is exactly what `RenderPipeline.render()` arranges for its *own* scene pass, by forcing
     * `NoToneMapping` and the working colour space for the duration and restoring them afterwards.
     * Rendering the scene ourselves puts it outside that window, so without this the renderer
     * applies ACES to the scene on the way into the buffer and the chain applies it again on the
     * way out. Double tone mapping does not look like a bug so much as a mood: a noon sky at 0.03
     * radiance is crushed to black by the first pass and there is nothing left for the second to
     * recover. Ask for it explicitly, and hand the real values back before the chain runs, because
     * the chain reads `renderer.toneMapping` to decide what to *apply*.
     */
    const toneMapping = this.renderer.toneMapping;
    const outputColorSpace = this.renderer.outputColorSpace;
    this.renderer.toneMapping = NoToneMapping;
    this.renderer.outputColorSpace = ColorManagement.workingColorSpace;

    const timing = this.measurePhases;
    const started = timing ? performance.now() : 0;

    // 1. The scene, into our own buffer, with motion vectors alongside it when TRAA wants them.
    this.renderer.setMRT(this.settings.post.taa ? SCENE_MRT : null);
    this.renderer.setRenderTarget(sceneTarget);
    this.renderer.render(this.scene, this.camera);
    this.renderer.setMRT(null);
    this.renderer.setRenderTarget(null);
    const afterScene = timing ? performance.now() : 0;

    // 2. The cloud layer, at half resolution, occluded by the depth from step 1.
    this.clouds?.render(this.renderer, this.camera);
    const afterClouds = timing ? performance.now() : 0;

    this.renderer.toneMapping = toneMapping;
    this.renderer.outputColorSpace = outputColorSpace;

    // 3. Composite, exposure, anti-alias, bloom, tone map.
    pipeline.render();

    if (timing) {
      const phases = this.phases;
      phases.sceneMs = afterScene - started;
      phases.cloudsMs = afterClouds - afterScene;
      phases.postMs = performance.now() - afterClouds;
    }
    return true;
  }

  dispose(): void {
    this.pipeline?.dispose();
    this.pipeline = null;
    this.sceneTarget?.dispose();
    this.sceneTarget = null;
  }
}
